<template>
  <div class="d-flex align-center mb-2">
    <b>{{ $t('PROJECT_SHEET.FILES_DESCRIPTION') }}</b>
  </div>
  <v-card class="pa-6">
    <div class="d-flex gap-2 mb-4">
      <text-filter v-model="filters.elementTitle" :label="$t('PROJECT_SHEET.FILTERS.ELEMENT_TITLE')" clearable prepend-inner-icon="far fa-search" style="flex: 1 1 30%"></text-filter>
      <text-filter v-model="filters.linkName" :label="$t('PROJECT_SHEET.FILTERS.ATTACHMENT_NAME')" clearable prepend-inner-icon="far fa-paperclip" style="flex: 1 1 30%"></text-filter>
      <select-filter v-model="filters.categories" :items="categoryFilterItems" :label="$t('PROJECT_SHEET.FILTERS.ATTACHMENT_TYPE')" prepend-inner-icon="far fa-file" multiple clearable style="flex: 1 1 30%">
        <template #selection="{ item: { raw: item }, index }">
          <span v-if="filters.categories.length == 1">{{ item.title }}</span>
          <span v-else-if="index === 0">{{ filters.categories.length }}/{{ categoryFilterItems.length }}</span>
        </template>
      </select-filter>
    </div>

    <div v-for="linkElement in filteredLinksElements" :key="linkElement.id" class="mb-2">
      <div class="small pointer" style="font-style: italic;" @click="openElement(linkElement.el)">
        {{ linkElement.title }}
      </div>
      <ul class="list-unstyled" style="margin-left: 16px; margin-top: 2px">
        <li v-for="linkData in linkElement.linksData" class="text-ellipsis" style="margin-bottom: 2px">
          <span>
            <link-icon :link="linkData.link" class="mr-2"></link-icon>
            <a :href="subdomainLinkUrl(linkData.link.url)" :title="linkData.link.name" target="_blank" rel="noopener">
              {{ linkData.displayName }}
            </a>

            <v-menu v-if="isPicture(linkData.link)" content-class="ml-2 rounded-sm" open-on-hover location="right" offset="8" transition="scale-transition" activator="parent">
              <div class="bg-surface" style="max-width: 400px">
                <img v-if="linkData.link.url" :src="linkData.link.url" :alt="$t('FILES.LINK_IMAGE_UNAVAILABLE')" style="width: 100%;">
              </div>
            </v-menu>
          </span>
        </li>
      </ul>
    </div>
    <div v-if="! filteredLinksElements.length" class="mt-2 text-medium-emphasis">{{ $t('PROJECT_SHEET.NO_FILE') }}</div>
  </v-card>
</template>

<script>
  import clientConfig from '@/client_customs/config';
  import LinkIcon from '@/components/Planning/partials/LinkIcon.vue';

  export default {
    components: {
      LinkIcon,
    },
    props: {
      planning: { type: Object, required: true },
    },
    emits: ['open-element'],
    data() {
      return {
        filters: {
          elementTitle: '',
          linkName: '',
          categories: [],
        },
      };
    },
    computed: {
      categoriesNames() {
        return {
          image: this.$t('FILES.IMAGE'),
          video: this.$t('FILES.VIDEO'),
          audio: this.$t('FILES.AUDIO'),
          pdf: this.$t('FILES.PDF'),
          spreadsheet: this.$t('FILES.SPREADSHEET'),
          word: this.$t('FILES.WORD_DOCUMENT'),
          archive: this.$t('FILES.ARCHIVE'),
          file: this.$t('FILES.OTHER_FILE'),
          link: this.$t('FILES.LINK'),
          onedrive: 'OneDrive',
          googledrive: 'Google Drive',
          dropbox: 'Dropbox',
        };
      },
      linksElements() {
        return this.planning.elements.filter(el => ! el.isType('macro') && el.getLinks() && el.getLinks().length)
          .sort((a, b) => (a.getLaneId() < b.getLaneId() ? -1 : 1))
          .map((el) => {
            return {
              id: el.id,
              el,
              title: el.getTitle() || this.$t('helpers.elDefaultTitle', { type: el.getType() }),
              linksData: el.getLinks().map(link => ({
                link,
                displayName: link.name || link.url,
                category: link.mime && this.$filters.mimeIcon(link.mime).replace(/^(far|fas|fab) fa-/, '') || link.type || 'file',
              })),
            };
          });
      },
      categoryFilterItems() {
        const categories = new Set();
        this.linksElements.forEach(item => item.linksData.forEach((linkData) => { categories.add(linkData.category); }));
        return Object.keys(this.categoriesNames).filter(category => categories.has(category)).map(category => ({ value: category, title: this.categoriesNames[category] || category }));
      },
      filteredLinksElements() {
        const elementTitleFilter = this.filters.elementTitle?.trim().toLowerCase();
        const linkNameFilter = this.filters.linkName?.trim().toLowerCase();
        const filteredLinksElements = [];
        this.linksElements.forEach((item) => {
          if (elementTitleFilter && ! item.title.toLowerCase().includes(elementTitleFilter)) return;

          const linksData = item.linksData.filter((linkData) => {
            if (this.filters.categories.length && ! this.filters.categories.includes(linkData.category)) return false;
            if (linkNameFilter && ! linkData.displayName.toLowerCase().includes(linkNameFilter)) return false;
            return true;
          });
          if (linksData.length) {
            filteredLinksElements.push({ ...item, linksData });
          }
        });

        return filteredLinksElements;
      },
    },
    methods: {
      openElement(el) {
        this.$emit('open-element', el);
      },
      isPicture(link) {
        if (link.mime && link.mime.includes('image')) return true;
        return /(http)s?:(\/\/[^"']*\.(?:png|jpg|jpeg|gif|png|svg)$)/i.test(link.url);
      },
      subdomainLinkUrl(url) {
        return clientConfig.subdomain && url?.startsWith('https://api.bubbleplan.net') ? `${url}?subdomain=${clientConfig.subdomain}` : url;
      },
    },
  };
</script>
